class PfShippingCountdown extends HTMLElement {
  constructor() {
    super();

    this._abortController = null;
    this._countdownInterval = null;

    this._config = {
      timezone: "Europe/Stockholm",
      leadTimeDays: 2,
    };

    this._elements = {
      countdownTimer: null,
      shippingDisplay: null,
      deliveryDisplay: null,
    };

    this._dayMap = {
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6,
    };
  }

  get cutoffTime() {
    const time = this.dataset.cutoffTime || "11:00";
    const [hours, minutes] = time.split(":").map(Number);
    return { hours, minutes };
  }

  get offDayStart() {
    return this._dayMap[this.dataset.offdayStart?.toLowerCase()] ?? 5; // Default: Friday
  }

  get weekStart() {
    return this._dayMap[this.dataset.weekStart?.toLowerCase()] ?? 1; // Default: Monday
  }

  get leadTimeDays() {
    return this._config.leadTimeDays;
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this._cacheElements();
    this._initialize();
  }

  disconnectedCallback() {
    this._cleanup();
  }

  _cacheElements() {
    this._elements.countdownTimer = this.querySelector(".countdown-timer");
    this._elements.shippingDisplay = this.querySelector(
      ".pm__order-shipping--dynamic"
    );
    this._elements.deliveryDisplay = this.querySelector(
      ".pm__order-date--dynamic"
    );
  }

  _initialize() {
    this._updateDisplay();
    this._startCountdown();
  }

  _cleanup() {
    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }

    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  _getStockholmTime() {
    const now = new Date();
    const stockholmStr = now.toLocaleString("en-US", {
      timeZone: this._config.timezone,
    });
    return new Date(stockholmStr);
  }

  _getStockholmDate(date) {
    const stockholmStr = date.toLocaleString("en-US", {
      timeZone: this._config.timezone,
    });
    return new Date(stockholmStr);
  }

  _isPastCutoff(stockholmTime) {
    const { hours, minutes } = this.cutoffTime;
    const cutoffMinutes = hours * 60 + minutes;
    const currentMinutes =
      stockholmTime.getHours() * 60 + stockholmTime.getMinutes();
    return currentMinutes >= cutoffMinutes;
  }

  _isOffDayStart(date) {
    return date.getDay() === this.offDayStart;
  }

  _isBetweenOffDayAndWeekStart(date) {
    const day = date.getDay();
    const offDay = this.offDayStart;
    const weekStart = this.weekStart;

    if (offDay < weekStart) {
      return day > offDay && day < weekStart;
    } else {
      return day > offDay || day < weekStart;
    }
  }

  _shouldHide(stockholmTime) {
    const day = stockholmTime.getDay();
    const isPastCutoff = this._isPastCutoff(stockholmTime);

    if (this._isOffDayStart(stockholmTime) && isPastCutoff) {
      return true;
    }

    if (this._isBetweenOffDayAndWeekStart(stockholmTime)) {
      return true;
    }

    return false;
  }

  _getNextShippingDay(fromDate) {
    const nextDay = new Date(fromDate);
    nextDay.setDate(nextDay.getDate() + 1);

    let attempts = 0;
    while (attempts < 7) {
      if (
        !this._isBetweenOffDayAndWeekStart(nextDay) &&
        !this._isOffDayStart(nextDay)
      ) {
        return nextDay;
      }
      nextDay.setDate(nextDay.getDate() + 1);
      attempts++;
    }

    return nextDay;
  }

  _getNextWeekStart(fromDate) {
    const date = new Date(fromDate);
    const daysToAdd = (this.weekStart - date.getDay() + 7) % 7;

    if (daysToAdd === 0) {
      date.setDate(date.getDate() + 7);
    } else {
      date.setDate(date.getDate() + daysToAdd);
    }

    return date;
  }

  _formatDate(date, stockholmTime) {
    const today = new Date(stockholmTime);
    today.setHours(0, 0, 0, 0);

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    const diffDays = Math.round((targetDate - today) / (1000 * 60 * 60 * 24));

    const translations = {
      today: window.productStrings?.today || "Today",
      tomorrow:
        window.productStrings?.tomorrow ||
        window.productStrings?.shipping_tomorrow ||
        "Tomorrow",
    };

    if (diffDays === 0) return translations.today;
    if (diffDays === 1) return translations.tomorrow;

    const month = date.toLocaleString("en-US", {
      month: "short",
    });
    const day = date.getDate();

    return `${month} ${day}`;
  }

  _formatDateRange(startDate, endDate) {
    const month = startDate.toLocaleString("en-US", {
      month: "short",
    });

    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    if (startDate.getMonth() === endDate.getMonth()) {
      return `${month} ${startDay}-${endDay}`;
    } else {
      const endMonth = endDate.toLocaleString("en-US", {
        month: "short",
      });
      return `${month} ${startDay} - ${endMonth} ${endDay}`;
    }
  }

  _calculateDates(stockholmTime) {
    const isPastCutoff = this._isPastCutoff(stockholmTime);

    const isOffDayStartAndPastCutoff =
      this._isOffDayStart(stockholmTime) && isPastCutoff;
    const isBetweenOffDays = this._isBetweenOffDayAndWeekStart(stockholmTime);

    let shippingDate, deliveryDate;

    if (isOffDayStartAndPastCutoff || isBetweenOffDays) {
      shippingDate = this._getNextWeekStart(stockholmTime);

      deliveryDate = new Date(shippingDate);
      deliveryDate.setDate(deliveryDate.getDate() + this.leadTimeDays - 1);
    } else if (isPastCutoff) {
      shippingDate = new Date(stockholmTime);
      shippingDate.setDate(shippingDate.getDate() + 1);

      deliveryDate = new Date(stockholmTime);
      deliveryDate.setDate(deliveryDate.getDate() + this.leadTimeDays);
    } else {
      shippingDate = new Date(stockholmTime);

      deliveryDate = new Date(stockholmTime);
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }

    return { shippingDate, deliveryDate };
  }

  _getNextCutoff(stockholmTime) {
    const { hours, minutes } = this.cutoffTime;
    const nextCutoff = new Date(stockholmTime);
    nextCutoff.setHours(hours, minutes, 0, 0);

    if (stockholmTime >= nextCutoff) {
      nextCutoff.setDate(nextCutoff.getDate() + 1);
    }

    return nextCutoff;
  }

  _updateDisplay() {
    const stockholmTime = this._getStockholmTime();

    if (this._shouldHide(stockholmTime)) {
      this.dataset.canHide = "true";
    } else {
      delete this.dataset.canHide;
    }

    this._updateCountdown(stockholmTime);

    this._updateDates(stockholmTime);
  }

  _updateCountdown(stockholmTime) {
    if (!this._elements.countdownTimer) return;

    const nextCutoff = this._getNextCutoff(stockholmTime);
    const now = new Date();

    const stockholmNow = this._getStockholmTime();
    const offset = now.getTime() - stockholmNow.getTime();
    const adjustedCutoff = new Date(nextCutoff.getTime() + offset);

    const diff = adjustedCutoff - now;

    if (diff <= 0) {
      this._elements.countdownTimer.textContent = "00:00:00";
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    this._elements.countdownTimer.textContent = `${hours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }

  _updateDates(stockholmTime) {
    const { shippingDate, deliveryDate } = this._calculateDates(stockholmTime);

    if (this._elements.shippingDisplay) {
      this._elements.shippingDisplay.textContent = this._formatDate(
        shippingDate,
        stockholmTime
      );
    }

    if (this._elements.deliveryDisplay) {
      const isPastCutoff = this._isPastCutoff(stockholmTime);

      if (isPastCutoff) {
        const endDate = new Date(deliveryDate);
        endDate.setDate(endDate.getDate() + 1);
        this._elements.deliveryDisplay.textContent = this._formatDateRange(
          deliveryDate,
          endDate
        );
      } else {
        this._elements.deliveryDisplay.textContent = this._formatDate(
          deliveryDate,
          stockholmTime
        );
      }
    }
  }

  _startCountdown() {
    this._updateDisplay();

    this._countdownInterval = setInterval(() => {
      if (this._abortController?.signal.aborted) {
        this._cleanup();
        return;
      }
      this._updateDisplay();
    }, 1000);
  }
}

if (!customElements.get("pf-shipping-countdown")) {
  customElements.define("pf-shipping-countdown", PfShippingCountdown);
}
